import React from 'react';
import { UserSearchForm } from '../types';

interface UserSearchFormProps {
  formData: UserSearchForm;
  onChange: (field: keyof UserSearchForm, value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  error: string | null;
}

export const UserSearchFormComponent: React.FC<UserSearchFormProps> = ({
  formData,
  onChange,
  onSubmit,
  isLoading,
  error
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  // 日付の妥当性チェック
  const validateDates = () => {
    if (!formData.startDate || !formData.endDate) {
      return null;
    }
    
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const diffDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return '終了日は開始日以降である必要があります';
    }
    if (diffDays > 61) {
      return '検索期間は最大2ヶ月までです';
    }
    return null;
  };

  const dateError = validateDates();
  const canSubmit = !isLoading && 
                     (formData.name || formData.email) && 
                     formData.startDate && 
                     formData.endDate && 
                     !dateError;

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">ユーザー検索</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="user-name" className="block text-sm font-medium text-gray-700 mb-1">
            ユーザー名（部分一致）
          </label>
          <input
            id="user-name"
            type="text"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="山田"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
        </div>

        <div>
          <label htmlFor="user-email" className="block text-sm font-medium text-gray-700 mb-1">
            メールアドレス（部分一致）
          </label>
          <input
            id="user-email"
            type="text"
            value={formData.email}
            onChange={(e) => onChange('email', e.target.value)}
            placeholder="example@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
        </div>

        <div className="text-xs text-gray-500 -mt-2">
          ※ ユーザー名またはメールアドレスのいずれかを入力してください
        </div>

        <div>
          <label htmlFor="user-start-date" className="block text-sm font-medium text-gray-700 mb-1">
            開始日 <span className="text-red-500">*</span>
          </label>
          <input
            id="user-start-date"
            type="date"
            value={formData.startDate}
            onChange={(e) => onChange('startDate', e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
        </div>

        <div>
          <label htmlFor="user-end-date" className="block text-sm font-medium text-gray-700 mb-1">
            終了日 <span className="text-red-500">*</span>
          </label>
          <input
            id="user-end-date"
            type="date"
            value={formData.endDate}
            onChange={(e) => onChange('endDate', e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
        </div>

        {dateError && (
          <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
            {dateError}
          </div>
        )}

        <div className="text-xs text-gray-500">
          ※ 検索期間は最大2ヶ月までです
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
            canSubmit
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isLoading ? '検索中...' : 'ユーザーを検索'}
        </button>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
            {error}
          </div>
        )}
      </form>
    </div>
  );
};
